<template>
  <template
    v-if="$store.state.challenges.detail.errorId !== Number(this.$route.params.id)"
  >
    <div class="challenge-detail overflow-y-auto h-screen" v-if="challenge && !isLoadingChallenge" ref="scrollable">
      <ChallengeHero :challenge="challenge"/>

      <Container class="tw-flex flex-col lg:flex-row gap-6 py-6">
        <!-- Left Sidebar -->
        <div class="w-full lg:w-[280px] shrink-0">
          <!-- Organisation Logo Card -->
          <div
            v-if="!challenge.isAnonymous && challenge.actor"
            class="bg-white rounded-lg shadow-md border border-transparent hover:border-primary cursor-pointer tw-flex flex-col items-center justify-center min-h-[250px] -mt-20 lg:-mt-24 relative z-10 mb-4"
            @click="goToActorDetail"
          >
            <ActorAvatar
              class="w-24 h-24 rounded-lg mt-8"
              variant="rounded"
              :actor-logo-url="challenge.actor.url"
              :actor="challenge.actor"
            />
            <div
              v-if="challenge?.actor?.description"
              class="p-6 text-sm text-gray-600 line-clamp-3"
              v-html="challenge?.actor?.description?.slice(0, 100)"
            />
          </div>

          <!-- SDGs -->
          <actor-detail-SDGs
            v-if="!challenge.isAnonymous"
            target="simplified-challenge"
            :class="!challenge.actor ? 'relative -top-14 left-4' : ''"
            :selected-goals="sdgs"
          />

          <!-- Contact Card -->
          <div
            v-if="challenge.contact && !challenge.isAnonymous"
            class="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-4"
          >
            <AvatarNew
              :user="challenge.contact"
              :show-details="true"
            />
            <div class="mt-4">
              <DsButton
                :disabled="!canStartConversation(challenge.contact)"
                class="w-full"
                variant="rounded"
                :label="$t('get_in_touch')"
                @click="startConversation(challenge.contact.id)"
              />
            </div>
          </div>

          <!-- Challenge Info Card -->
          <div class="bg-white rounded-lg shadow-md border border-gray-200 mb-4">
            <h3 class="text-center font-bold border-b border-gray-200 w-4/5 mx-auto py-4 mt-4">
              {{ challengeLabel }} info
            </h3>

            <div class="p-4">
              <ChallengeCountdown :challenge="challenge"/>

              <!-- End Date -->
              <div v-if="challengeEndDate" class="tw-flex items-center gap-2 mt-4 text-sm">
                <Icon name="clock"/>
                <strong>{{ challengeEndDate }}</strong>
              </div>

              <!-- Goals -->
              <div
                v-if="challenge && challengeGoals.length > 0"
                class="tw-flex items-start gap-2 mt-3 text-sm"
              >
                <Icon name="hand-shake" class="shrink-0 mt-0.5"/>
                <strong
                  v-tooltip.top="challengeGoals.length > 3 ? challengeGoals.join(', ') : ''"
                >
                  {{ challengeGoals.length > 3 ? challengeGoals.slice(0, 3).join(', ') + '...' : challengeGoals.slice(0, 3).join(', ') }}
                </strong>
              </div>

              <!-- Geographical Labels -->
              <div
                v-if="challenge.geographicalLabels && challenge.geographicalLabels.length > 0"
                class="tw-flex items-start gap-2 mt-3 text-sm"
              >
                <Icon name="map-marker" class="shrink-0 mt-0.5"/>
                <strong>{{ geographicalLabels.join(', ') }}</strong>
              </div>

              <!-- Actions -->
              <div class="tw-flex flex-col gap-2 mt-6">
                <DsButton
                  v-if="challenge.id && canUserEditChallenge"
                  variant="rounded"
                  class="w-full"
                  :label="$t('actor_edit')"
                  icon="edit"
                  @click="showEditChallengePanel"
                />
                <DsButton
                  v-if="isLoggedIn && canUserAccessChallengeDetail"
                  :variant="challenge.isFollowing ? 'rounded-secondary' : 'rounded'"
                  class="w-full"
                  :label="challenge.isFollowing ? $t('intelligence_simplified_following') : $t('intelligence_simplified_follow')"
                  :loading="togglingPinStatus"
                  @click.stop="togglePinStatus"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="flex-1 min-w-0">
          <!-- Tabs -->
          <div class="bg-primary-extra-lightest rounded-lg overflow-hidden">
            <div class="tw-flex flex-wrap">
              <router-link
                to="#overview"
                class="flex-1 min-w-[100px] h-12 leading-[48px] flex-wrap text-center content-center text-primary cursor-pointer px-3 md:px-5 text-sm md:text-base no-underline truncate hover:bg-primary-lightest hover:font-medium"
                :class="{'bg-primary-lightest font-medium': $route.hash === '#overview' || !$route.hash}"
                @click="subNavSection = ''"
              >
                {{ $t('challenges_navigation_overview') }}
              </router-link>
              <router-link
                to="#qanda"
                class="flex-1 min-w-[100px] h-12 leading-[48px] flex-wrap text-center content-center text-primary cursor-pointer px-3 md:px-5 text-sm md:text-base no-underline truncate hover:bg-primary-lightest hover:font-medium"
                :class="{'bg-primary-lightest font-medium': $route.hash === '#qanda'}"
                @click="subNavSection = 'q&a'"
              >
                {{ $t('challenges_navigation_qanda') }}
              </router-link>
              <router-link
                to="#updates"
                class="flex-1 min-w-[100px] h-12 leading-[48px] flex-wrap text-center content-center text-primary cursor-pointer px-3 md:px-5 text-sm md:text-base no-underline truncate hover:bg-primary-lightest hover:font-medium"
                :class="{'bg-primary-lightest font-medium': $route.hash === '#updates'}"
                @click="subNavSection = 'updates'"
              >
                {{ $t('challenges_navigation_updates') }}
              </router-link>
              <router-link
                to="#documents"
                class="flex-1 min-w-[100px] h-12 leading-[48px] flex-wrap text-center content-center text-primary cursor-pointer px-3 md:px-5 text-sm md:text-base no-underline truncate hover:bg-primary-lightest hover:font-medium"
                :class="{'bg-primary-lightest font-medium': $route.hash === '#documents'}"
                @click="subNavSection = 'documents'"
              >
                {{ $t('challenges_navigation_documents') }}
              </router-link>
              <router-link
                v-if="canDisplayMatchesTab"
                to="#match"
                class="flex-1 min-w-[100px] h-12 leading-[48px] flex-wrap text-center content-center text-primary cursor-pointer px-3 md:px-5 text-sm md:text-base no-underline truncate hover:bg-primary-lightest hover:font-medium"
                :class="{'bg-primary-lightest font-medium': $route.hash === '#match'}"
                @click="subNavSection = 'match'"
              >
                {{ $t('challenges_navigation_match') }}
              </router-link>
            </div>
          </div>

          <!-- Tab Content -->
          <div class="py-4 md:py-6">
            <!-- Overview Tab -->
            <div v-if="subNavSection === ''">
              <div v-if="challenge.description" class="mb-6">
                <p class="text-base leading-relaxed" v-html="challenge.description"></p>
              </div>

              <div v-if="challenge.background && challenge.background.length > 8" class="mb-6">
                <h2 class="text-xl font-semibold mb-3">{{ $t('challenge_background_of_the_challenge', { challenge: challengeLabel }) }}</h2>
                <p class="text-base leading-relaxed" v-html="challenge.background"></p>
              </div>

              <div v-if="challenge.objective && challenge.objective.length > 8" class="mb-6">
                <h2 class="text-xl font-semibold mb-3">{{ $t('challenges_what_are_we_looking_for') }}</h2>
                <p class="text-base leading-relaxed" v-html="challenge.objective"></p>
              </div>
            </div>

            <!-- Q&A Tab -->
            <div v-else-if="subNavSection === 'q&a'">
              <Comments
                :comments="comments"
                :comments-limit="100"
                :resource-id="String(challenge.id)"
                :show-title="false"
                :only-show-add-comment-input="false"
                @add-comment="addComment"
                @delete-comment="fetchComments"
              />
            </div>

            <!-- Updates Tab -->
            <div v-else-if="subNavSection === 'updates'">
              <div class="tw-flex flex-col">
                <LoadingNew v-if="isLoadingUpdates"/>

                <template v-else-if="updates.length > 0">
                  <div
                    v-for="update in updates"
                    :key="update.id"
                    class="border border-primary rounded-lg p-4 mt-3 first:mt-0"
                  >
                    <div class="tw-flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <AvatarNew :user="update.user" :show-details="true"/>
                      <div class="tw-flex items-center gap-2 text-sm text-gray-500">
                        {{ fromNow(update.createdAt) }}
                        <DsButton
                          v-if="update.user.id === userId"
                          class="button--minimal button--default"
                          icon="trash"
                          size="small"
                          iconSize="18"
                          @click="deleteChallengeUpdate(update)"
                        />
                      </div>
                    </div>
                    <div class="mt-3">
                      <p v-html="update.content"></p>
                    </div>
                  </div>
                </template>

                <div v-else class="bg-primary-extra-lightest w-full p-6 rounded-lg">
                  <h4 class="text-gray-600">No updates yet</h4>
                </div>

                <div v-if="canUserEditChallenge" class="mt-4">
                  <Comments
                    :only-show-add-comment-input="true"
                    @add-comment="addUpdate"
                  />
                </div>
              </div>
            </div>

            <!-- Documents Tab -->
            <div v-else-if="subNavSection === 'documents'">
              <div class="tw-flex flex-col">
                <div v-if="canDisplayDocumentsTab" class="tw-flex justify-end mb-4">
                  <DsButton
                    variant="rounded"
                    size="small"
                    label="Add document"
                    icon="plus"
                    @click="openCreateResourcePanel"
                  />
                </div>

                <LoadingNew v-if="isLoadingDocuments"/>

                <template v-else-if="documents.length > 0">
                  <div
                    v-for="document in documents"
                    :key="document.id"
                    class="tw-flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-primary rounded-lg p-4 mt-3 first:mt-0 cursor-pointer hover:shadow-md transition-shadow"
                    @click="openResource(document)"
                  >
                    <div class="flex-1 min-w-0">
                      <strong class="block truncate">{{ document.title }}</strong>
                      <p class="text-sm text-gray-600 line-clamp-2 mt-1">{{ document.description }}</p>
                    </div>
                    <div class="tw-flex items-center gap-2 shrink-0">
                      <DsButton
                        variant="rounded-secondary"
                        size="extra-small"
                        class="bg-primary-extra-lightest text-primary"
                        :label="getFileType(document)"
                        :disabled="true"
                      />
                      <DsButton
                        v-if="isMember || challenge.creator.id === userId"
                        variant="rounded"
                        icon="edit"
                        size="small"
                        @click.stop="showEditResourcePanel(document)"
                      />
                      <DsButton
                        v-if="isMember || challenge.creator.id === userId"
                        variant="rounded"
                        icon="trash"
                        size="small"
                        @click.stop="showDeleteResourceConfirmationModal(document)"
                      />
                    </div>
                  </div>
                </template>

                <div v-else class="bg-primary-extra-lightest w-full p-6 rounded-lg">
                  <h4 class="text-gray-600">No documents yet</h4>
                </div>
              </div>
            </div>

            <!-- Match Tab -->
            <ChallengeMatchTab
              v-else-if="subNavSection === 'match'"
              :challenge-id="challengeId"
              :challenge="challenge"
            />
          </div>
        </div>
      </Container>

      <!-- Similar Challenges Section -->
      <div
        v-if="similarAndNextChallenges.length > 0"
        class="py-8 mb-8"
      >
        <Container>
          <h3 class="text-xl font-semibold mb-4">{{ $t('more_actor_profiles') }}</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ChallengeCardNew
              v-for="similarChallenge in similarAndNextChallenges.slice(0, 3)"
              :key="similarChallenge.id"
              :challenge="similarChallenge"
            />
          </div>
        </Container>
      </div>
    </div>
  </template>

  <template v-else-if="isLoadingChallenge">
    <LoadingNew/>
  </template>

  <template
    v-else-if="$store.state.challenges.detail.errorStatusCode && $store.state.challenges.detail.errorStatusCode == 404"
  >
    <div class="text-center my-auto p-8">
      <h3 class="text-xl font-semibold">This {{ challengeLabel }} has been removed.</h3>
      <a href="/challenges" class="text-primary hover:underline mt-2 inline-block">View all {{ challengesLabel }}</a>
    </div>
  </template>

  <template v-else>
    <div class="text-center my-auto p-8">
      <h3 class="text-xl font-semibold">You are not allowed to view the contents of this {{ challengeLabel }}</h3>
      <a href="#" @click="showLoginModal()" v-if="!isLoggedIn" class="text-primary hover:underline mt-2 inline-block">Log in</a>
      <a href="/profile/subscription#billing" v-else-if="canShowSubscriptions" class="text-primary hover:underline mt-2 inline-block">Upgrade</a>
    </div>
  </template>
</template>

<script lang="ts">
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import { defineComponent } from 'vue'
  import UserMixin from '@/util/UserMixin'
  import ChallengeMixin from '@/util/ChallengeMixin'
  import ChallengeHero from '@/components/Challenges/ChallengeHero.vue'
  import { getLogoDevLogoUrl } from '../../util/actor'
  import Container from '@/components/Container/Container.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { fromNow, getChallengeTimeDifferenceValue } from '../../util/date'
  import ActorDetailSDGs from '@/pages/ActorDetail/ActorDetailSDGs.vue'
  import BasicHero from '@/components/BasicHero/BasicHero.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import _uniqBy from 'lodash/uniqBy'
  import {
    addChallengeUpdate,
    createComment,
    fetchChallengeUpdates,
    fetchSimilarChallenges,
    getComments,
    removeChallengeUpdate,
    updatePinStatus
  } from '@/Domain/Challenge/Api/challenges'
  import ChallengeCountdown from '@/components/Challenges/ChallengeCountdown.vue'
  import KnowledgeBaseSimplified from '@/components/Dashboard/KnowledgeBaseSimplified.vue'
  import Comments from '@/components/Comments.vue'
  import moment from 'moment'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { fetchKnowledgeBaseData } from '@/api/knowledge-base'
  import MODAL_IDS from '@/constants/modal-ids'
  import { deleteEcosystemFile } from '@/api/files'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'
  import Tabs from '@/components/Tabs/Tabs.vue'
  import Tag from '@/components/Tag/Tag.vue'
  import Avatar from '@/components/Avatar/Avatar.vue'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import UserActivityMixin from '@/Domain/User/Mixins/UserActivityMixin'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import ActorBlock from '@/components/Dashboard/EmailReports/ActorBlock.vue'
  import ActorSuggestionCard from '@/components/SpottingAreas/ActorSuggestionCard.vue'
  import { ConversationsMixin } from '@/util/ConversationsMixin'
  import ChallengeMatchTab from '@/components/Challenges/ChallengeMatchTab.vue'

  export default defineComponent({
    name: 'challenge-detail-new',
    mixins: [TranslationsMixin, UserMixin, ChallengeMixin, UserActivityMixin, ConversationsMixin],
    components: {
      ChallengeMatchTab,
      ActorSuggestionCard,
      ActorBlock,
      ActorAvatar,
      AvatarNew,
      Avatar,
      Tag,
      Tabs,
      LoadingNew,
      Comments,
      KnowledgeBaseSimplified,
      ChallengeCountdown,
      ChallengeCardNew,
      BasicHero,
      ActorDetailSDGs,
      Icon,
      Container,
      ChallengeHero
    },
    data() {
      return {
        subNavSection: '',
        similarLocalChallenges: [],
        comments: [],
        commentsCount: 0,
        togglingPinStatus: false,
        documents: [],
        isLoadingDocuments: false,
        isLoadingUpdates: false,
        updates: [],
        handleChallengeUpdateDelete: null,
        handleFetchDocuments: null,
        handleFileDelete: null,
      }
    },
    computed: {
      canDisplayDocumentsTab(): boolean {
        return this.isOwner || this.isMember || (this.challenge.creator && this.userId === this.challenge.creator.id)
      },
      canDisplayMatchesTab(): boolean {
        return this.isOwner || this.isMember || (this.challenge.creator && this.userId === this.challenge.creator.id)
      },
      challenge() {
        return this.$store.state.challenges.detail.data
      },
      geographicalLabels(): String[] {
        return this.challenge.geographicalLabels.map(geography => {
          return geography.label
        })
      },
      canShowSubscriptions(): boolean {
        return this.$store.getters.hasAccessToEcosystemMemberPackages
      },
      challengeEndDate(): String {
        if (this.challenge.endDate) {
          return moment(this.challenge.endDate).format('LL');
        }
        return ''
      },
      similarAndNextChallenges() {
        const result = this.similarLocalChallenges.concat(this.challenges).filter(challenge => challenge.id !== Number(this.$route.params.id))
        return _uniqBy(result, 'id')
      },
      headerStyle() {
        return {
          padding: '0 0 0.5rem 0',
          justifyContent: 'flex-start',
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 1) !important',
          width: '100%',
          borderBottom: '1px solid var(--primary-community)'
        }
      },
      sdgs(): String[] {
        if (!Array.isArray(this.challenge.sdgs)) return

        if (!this.challenge.sdgs.length) {
          return []
        }

        return this.challenge.sdgs.map((sdg) => {
          return sdg.label
        })
      }
    },
    methods: {
      fromNow,
      getChallengeTimeDifferenceValue,
      getClearbitActorLogo: getLogoDevLogoUrl,
      showLoginModal() {
        window.redirect_uri = window.location.href
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
      },
      deleteChallengeUpdate(update) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: 'challenge-update',
          resource: update,
          modalContextType: 'challenge-update',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      addUpdate(content) {
        addChallengeUpdate(this.challengeId, { content: content, challenge_id: this.challengeId })
          .then(() => {
            this.fetchUpdates()
          })
      },
      goToActorDetail() {
        if (this.challenge.actor) {
          window.open(`/actors/${this.challenge.actor.elastic_id}`, '_blank').focus()
        }
      },
      getFileType(document) {
        if (document.meta.find((doc) => doc.key === 'file_type') && document.meta.find((doc) => doc.key === 'file_type').value && document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1]) {
          if (document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1].includes('sheet')) {
            return 'Spreadsheet'
          }
          return document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1]
        }

        if (document.meta.find((doc) => doc.key === 'file_type') && document.meta.find((doc) => doc.key === 'file_type').value) {
          return document.meta.find((doc) => doc.key === 'file_type').value
        }

        return ''
      },
      openResource(document) {
        if (document.meta && document.meta.length > 0 && document.meta[0].value && document.meta[0].value === 'link') {
          if (document.meta.find((data) => data.key === 'url').value != null) {
            window.open(document.meta.find((data) => data.key === 'url').value, '_blank')
            return
          }
        }

        window.open(`${window.location.origin}/api/files/${document.sql_media_id}`, '_blank')
      },
      showDeleteResourceConfirmationModal(resource) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          body: 'Do you want to delete this resource?',
          resource: resource,
          modalContextType: 'file'
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      showEditResourcePanel(document) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          'component': 'edit-resource-panel',
          metaData: { resource: document },
        })
      },
      openCreateResourcePanel() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'create-resource-panel',
          metaData: 'resource',
        })
      },
      togglePinStatus() {
        this.togglingPinStatus = true
        updatePinStatus(this.challenge.id)
          .then((response) => {
            this.challenge.isFollowing = response.isFollowing
          })
          .finally(() => {
            this.togglingPinStatus = false
          })
      },
      fetchSimilarChallenges() {
        fetchSimilarChallenges(this.$route.params.id).then(result => {
          this.similarLocalChallenges = result
        })
      },
      fetchComments() {
        return getComments(Number(this.$route.params.id))
          .then(data => {
            this.comments = data.comments
            this.commentsCount = data.count
          })
          .catch(err => {
            // log the error to sentry
          })
      },
      addComment(comment) {
        createComment(this.challenge.id, comment)
          .then(() => {
            this.fetchComments()
          })
          .catch(errors => {
            this.errors = errors
          })
      },
      async fetchDocuments() {
        this.isLoadingDocuments = true
        let formattedFilter = []
        if (this.challengeId) {
          formattedFilter['linked_challenge_id'] = this.challengeId
        }

        try {
          await fetchKnowledgeBaseData(formattedFilter)
            .then((response) => {
              if (response && response.data && response.data.results) {
                this.documents = response.data.results
              }
            })
            .finally(() => {
              this.isLoadingDocuments = false
            })
        } catch (e) {
          console.log(e)
          this.isLoadingDocuments = false
        }
      },
      deleteResource(resource) {
        if (!resource.sql_media_id) {
          return
        }

        this.deleteEcosystemFile(resource.sql_media_id)
      },
      deleteEcosystemFile(fileId) {
        if (!fileId) {
          return
        }

        deleteEcosystemFile(fileId)
          .then(response => {
            this.resourceToDelete = {}
            setTimeout(() => {
              this.fetchDocuments()
            }, 500)
          })
          .catch(errors => {
            console.log(errors)
          })
      },
      fetchUpdates() {
        if (!this.challengeId) {
          return
        }

        this.isLoadingUpdates = true

        fetchChallengeUpdates(this.challengeId)
          .then((response) => {
            this.updates = response
          })
          .finally(() => {
            this.isLoadingUpdates = false
          })
      },
    },
    async beforeRouteUpdate(to, from, next) {
      if (to.name === 'challenges-detail') {
        this.$route.params.id = to.params.id
        this.fetchChallengeDetail()
      }
      next()
    },
    async mounted() {
      await this.fetchChallengeDetail(this.$route.params.id)
      await this.fetchComments()
      await this.fetchDocuments()
      this.fetchUpdates()
      this.fetchSimilarChallenges()

      this.handleChallengeUpdateDelete = (context) => {
        removeChallengeUpdate(context.resource.challengeId, context.resource.id)
          .then(() => {
            this.fetchUpdates()
          })
      }

      this.handleFetchDocuments = (challengeId) => {
        if (Number(challengeId) === Number(this.challengeId)) {
          setTimeout(() => {
            this.fetchDocuments()
          }, 500)
        }
      }

      this.handleFileDelete = (modalContext) => {
        this.deleteResource(modalContext.resource)
      }

      this.$bus.on('challengeUpdateDeleteConfirmation', this.handleChallengeUpdateDelete)
      this.$bus.on('fetch-challenge-documents', this.handleFetchDocuments)
      this.$bus.on('fileDeleteConfirmation', this.handleFileDelete)

      if (this.$route.hash) {
        if (this.$route.hash === '#qanda') {
          this.subNavSection = 'q&a'
          return
        }

        if (this.$route.hash === '#overview') {
          this.subNavSection = ''
          return
        }

        this.subNavSection = this.$route.hash.replace('#', '')
      }
    },
    beforeUnmount() {
      if (this.handleFetchDocuments) {
        this.$bus.off('fetch-challenge-documents', this.handleFetchDocuments)
      }
      if (this.handleFileDelete) {
        this.$bus.off('fileDeleteConfirmation', this.handleFileDelete)
      }
      if (this.handleChallengeUpdateDelete) {
        this.$bus.off('challengeUpdateDeleteConfirmation', this.handleChallengeUpdateDelete)
      }
    },
    watch: {
      async challengeId() {
        if (!this.$route.params.id || Number.isNaN(this.$route.params.id)) {
          return
        }

        await this.fetchChallengeDetail(this.$route.params.id)
        this.fetchSimilarChallenges()
        await this.fetchComments()
        await this.fetchDocuments()
        this.fetchUpdates()

        this.subNavSection = ''

        if (this.$refs.scrollable) {
          this.$refs.scrollable.scrollTop = 0
        }
      }
    }
  })
</script>

<style scoped>
  /* Custom color classes using CSS variables */
  .text-primary {
    color: var(--primary-community, #3b82f6);
  }

  .bg-primary {
    background-color: var(--primary-community, #3b82f6);
  }

  .bg-primary-extra-lightest {
    background-color: var(--primary-community-extra-lightest, #eff6ff);
  }

  .bg-primary-lightest {
    background-color: var(--primary-community-lightest, #dbeafe);
  }

  .border-primary {
    border-color: var(--primary-community, #3b82f6);
  }

  .hover\:border-primary:hover {
    border-color: var(--primary-community, #3b82f6);
  }

  .hover\:bg-primary-lightest:hover {
    background-color: var(--primary-community-lightest, #dbeafe);
  }

  /* Line clamp utilities */
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
